import React, { useEffect, useRef } from 'react';
import { fetchWithAutoRefresh } from "auth/KeycloakService";
import { useNavigate, useLocation } from 'react-router-dom';
import * as Sentry from "@sentry/react";

// Sentry logging helpers
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'oauth-callback' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'oauth-callback' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'oauth-callback',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `OAuth callback action: ${action}`,
    category: 'auth-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `OAuth callback performance: ${operation} took ${duration}ms`,
    category: 'auth-performance',
    level: 'info',
    data: { ...context, duration }
  });
};

const logSecurityEvent = (event, context = {}) => {
  Sentry.captureMessage(`OAuth callback security event: ${event}`, {
    level: 'warning',
    tags: { 
      section: 'oauth-security',
      event_type: 'security_event'
    },
    extra: context
  });
};

const BACKEND_URL = 'https://barrack.ai';

const Callback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const hasHandled = useRef(false);

  useEffect(() => {
    if (hasHandled.current) return;
    hasHandled.current = true;

    const componentStartTime = performance.now();
    
    logInfo('OAuth callback component mounted', {
      pathname: location.pathname,
      searchParams: !!location.search,
      timestamp: new Date().toISOString()
    });
    
    logUserAction('callback_component_mounted', {
      hasSearchParams: !!location.search
    });

    // Set OAuth callback context for Sentry
    Sentry.setContext("oauth-callback", {
      pathname: location.pathname,
      hasSearchParams: !!location.search,
      mountedAt: new Date().toISOString()
    });

    const handleCallback = async () => {
      const callbackStartTime = performance.now();
      
      const params = new URLSearchParams(location.search);
      const code = params.get('code');
      const state = params.get('state');
      const provider = params.get('provider') || 'google';
      
      logInfo('Processing OAuth callback parameters', {
        hasCode: !!code,
        hasState: !!state,
        provider: provider,
        searchParamsCount: params.size
      });
      
      logUserAction('callback_processing_started', {
        provider: provider,
        hasCode: !!code,
        hasState: !!state
      });

      if (!code || !state) {
        const securityContext = {
          hasCode: !!code,
          hasState: !!state,
          provider: provider,
          searchParams: location.search,
          userAgent: navigator.userAgent,
          referrer: document.referrer
        };
        
        logSecurityEvent('oauth_callback_missing_parameters', securityContext);
        
        logError('Missing required OAuth parameters', {
          hasCode: !!code,
          hasState: !!state,
          provider: provider,
          paramCount: params.size
        });
        
        logUserAction('callback_failed_missing_params', {
          hasCode: !!code,
          hasState: !!state,
          provider: provider
        });
        
        
        navigate('/login', {
          state: { error: 'Invalid authentication response' },
          replace: true
        });
        return;
      }

      try {
        const apiCallStartTime = performance.now();
        
        logInfo('Making OAuth callback API request', {
          provider: provider,
          backendUrl: BACKEND_URL
        });
        
        logUserAction('callback_api_request_started', {
          provider: provider
        });
        
        const callbackResponse = await fetchWithAutoRefresh(
          `${BACKEND_URL}/api/auth/oauth/google/callback?code=${code}&state=${state}`,
          {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Accept': 'application/json'
            }
          }
        );
        
        const apiCallEndTime = performance.now();
        const apiCallDuration = apiCallEndTime - apiCallStartTime;
        
        logPerformance('oauth_callback_api_call', apiCallDuration, {
          provider: provider,
          responseOk: callbackResponse.ok,
          status: callbackResponse.status
        });

        if (!callbackResponse.ok) {
          const errorText = await callbackResponse.text();
          
          logError('Backend OAuth callback failed', {
            provider: provider,
            status: callbackResponse.status,
            statusText: callbackResponse.statusText,
            hasErrorText: !!errorText,
            apiCallDuration: `${apiCallDuration.toFixed(2)}ms`
          });
          
          logUserAction('callback_api_request_failed', {
            provider: provider,
            status: callbackResponse.status,
            duration: `${apiCallDuration.toFixed(2)}ms`
          });
          
          throw new Error('Failed to exchange code for tokens');
        }

        const parseStartTime = performance.now();
        const callbackResult = await callbackResponse.json();
        const parseEndTime = performance.now();
        const parseDuration = parseEndTime - parseStartTime;
        
        logPerformance('oauth_callback_response_parse', parseDuration, {
          provider: provider,
          hasResult: !!callbackResult,
          success: !!callbackResult.success
        });

        if (callbackResult.success) {
          const totalDuration = parseEndTime - callbackStartTime;
          const componentTotalDuration = parseEndTime - componentStartTime;
          
          logPerformance('oauth_callback_success_complete', totalDuration, {
            provider: provider,
            hasEmail: !!callbackResult.email,
            hasProvider: !!callbackResult.provider
          });
          
          logPerformance('oauth_callback_component_complete', componentTotalDuration, {
            provider: provider,
            success: true
          });
          
          logInfo('OAuth callback completed successfully', {
            provider: callbackResult.provider || provider,
            hasEmail: !!callbackResult.email,
            totalDuration: `${totalDuration.toFixed(2)}ms`,
            componentDuration: `${componentTotalDuration.toFixed(2)}ms`
          });
          
          logUserAction('callback_success', {
            provider: callbackResult.provider || provider,
            totalDuration: `${totalDuration.toFixed(2)}ms`,
            componentDuration: `${componentTotalDuration.toFixed(2)}ms`
          });

          // Set OAuth success context
          Sentry.setContext("oauth-callback-success", {
            provider: callbackResult.provider || provider,
            hasEmail: !!callbackResult.email,
            completedAt: new Date().toISOString(),
            totalDuration: totalDuration
          });
          
          // Dispatch auth state change event
          window.dispatchEvent(new CustomEvent('auth:stateChanged', {
            detail: {
              authenticated: true,
              user: {
                email: callbackResult.email,
                provider: callbackResult.provider
              }
            }
          }));
          
          logUserAction('auth_state_changed_dispatched', {
            provider: callbackResult.provider || provider,
            authenticated: true
          });
          
          logInfo('Navigating to home page after successful OAuth', {
            provider: callbackResult.provider || provider
          });
          
          navigate('/', { replace: true });
          
        } else {
          logError('OAuth callback result indicated failure', {
            provider: provider,
            hasError: !!callbackResult.error,
            errorMessage: callbackResult.error || 'Unknown error',
            resultKeys: Object.keys(callbackResult)
          });
          
          logUserAction('callback_result_failed', {
            provider: provider,
            error: callbackResult.error || 'Unknown error'
          });
          
          throw new Error(callbackResult.error || 'Authentication failed');
        }
      } catch (error) {
        const errorEndTime = performance.now();
        const errorDuration = errorEndTime - callbackStartTime;
        const componentErrorDuration = errorEndTime - componentStartTime;
        
        logPerformance('oauth_callback_error', errorDuration, {
          provider: provider,
          errorName: error.name,
          errorType: error.constructor.name
        });
        
        logPerformance('oauth_callback_component_error', componentErrorDuration, {
          provider: provider,
          errorName: error.name
        });
        
        logError('OAuth callback processing failed', {
          provider: provider,
          errorName: error.name,
          errorMessage: error.message,
          errorStack: error.stack,
          processingDuration: `${errorDuration.toFixed(2)}ms`,
          componentDuration: `${componentErrorDuration.toFixed(2)}ms`,
          isNetworkError: error.name === 'TypeError' && error.message.includes('fetch'),
          timestamp: new Date().toISOString()
        }, error);
        
        logUserAction('callback_processing_failed', {
          provider: provider,
          errorName: error.name,
          errorMessage: error.message,
          duration: `${errorDuration.toFixed(2)}ms`
        });

        // Set OAuth failure context
        Sentry.setContext("oauth-callback-failure", {
          provider: provider,
          errorName: error.name,
          errorMessage: error.message,
          failedAt: new Date().toISOString(),
          processingDuration: errorDuration
        });
        
        logInfo('Navigating to login page after OAuth failure', {
          provider: provider,
          errorMessage: error.message
        });
        
        logUserAction('navigating_to_login_after_error', {
          provider: provider,
          errorType: error.name
        });
        
        navigate('/login', {
          state: { error: error.message || 'Authentication failed' },
          replace: true
        });
      }
    };

    handleCallback();
  }, [location, navigate]);

  // Component cleanup logging
  useEffect(() => {
    return () => {
      logInfo('OAuth callback component unmounting', {
        wasHandled: hasHandled.current
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-white flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-t-transparent border-white rounded-full animate-spin mx-auto" />
        <p className="mt-4">Processing authentication...</p>
        <p className="mt-2 text-sm text-gray-400">Please wait while we complete your request</p>
      </div>
    </div>
  );
};

export default Callback;